// lib
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

// import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

// components
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'
import { Slider } from 'primereact/slider'
import SelectOverlay from '@aytm/app/stats/page/sidebar/components/SelectOverlay'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// lib
import { removeTagsFromText } from '@aytm/app/stats/page/components/lib/utils'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

const THRESHOLD_OPTIONS = [
  { key: '0.2', text: '20%' },
  { key: '0.25', text: '25%' },
  { key: '0.333', text: '33.3%' },
  { key: '0.5', text: '50%' },
  { key: '0.6', text: '60%' },
  { key: '0.7', text: '70%' },
  { key: '0.8', text: '80%' }
]
const DEFAULT_THRESHOLD_OPTION = '0.7'

const DEFAULT_ITEMS_PER_SET = 4

const EXPORT_TYPES = [
  'turf',
  'conjoint',
  'emd',
  'ec',
  'concept_lab',
  'concept_mapping',
  'tracker_lab',
  'tracker_mapping'
]

// a mapping-mode question exports the same workbook as its Lab; the export service has no mapping writer
const LAB_EXPORT_TYPES = { concept_mapping: 'concept_lab', tracker_mapping: 'tracker_lab' }

const SpecificOptions = ({ report, options, exportType, processing, disabled, onExport }) => {
  const onExportClick = useCallback(() => {
    'function' == typeof onExport &&
      onExport({
        fileType: options.fileType,
        exportType: options.exportType,
        options: {
          ...options.exportOptions,
          method: options.key
        }
      })
  }, [options, onExport])

  return (
    <div className={style.section}>
      <div className={style.row}>
        <Button
          data-testid={`stat-exports-${exportType}-export`}
          className="p-button-rounded p-button-blue"
          label={EXPORT_TYPES.includes(processing) ? 'Downloading' : 'Download file'}
          loading={EXPORT_TYPES.includes(processing)}
          disabled={null !== processing || disabled}
          onClick={null !== processing || disabled ? null : onExportClick}
        />
      </div>

      {!report.current.schema.length ? (
        <div className={`${style.row} ${style['row-error']}`}>
          You need at least 1 question in order to use these features
        </div>
      ) : null}
    </div>
  )
}

SpecificOptions.propTypes = {
  report: PropTypes.object,
  options: PropTypes.object,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const SpecificMdOptions = ({
  root,
  report,
  features,
  question,
  options,
  exportType,
  processing,
  disabled,
  onExport
}) => {
  const [method, setMethod] = useState(features.special_export.config?.turf?.default || options[0].key)
  const [threshold, setThreshold] = useState(null)

  const thresholdRef = useRef(null)
  thresholdRef.current = threshold

  const methodObj = useMemo(() => options.find(option => option.key == method) || null, [method, options])

  const onExportClick = useCallback(() => {
    'function' == typeof onExport &&
      onExport({
        fileType: methodObj.fileType,
        exportType: methodObj.exportType,
        options: { ...methodObj.exportOptions, method, threshold }
      })
  }, [methodObj, method, threshold, onExport])

  useEffect(
    () =>
      methodObj.exportOptions.threshold
        ? setThreshold(null === thresholdRef.current ? DEFAULT_THRESHOLD_OPTION : thresholdRef.current)
        : setThreshold(null),
    [methodObj]
  )

  return (
    <>
      <div className={style.section}>
        {options.map((item, index) => (
          <div key={`${item.key}-${index}`} className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid={`stat-exports-${exportType}-method-${item.key}`}
                className="p-radiobutton-sm"
                aria-labelledby={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                value={item.key}
                checked={method == item.key}
                disabled={disabled}
                onChange={disabled ? null : () => setMethod(item.key)}
              />
              <span
                data-testid={`stat-exports-${exportType}-method-${item.key}-label`}
                id={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': disabled
                })}
                onClick={disabled ? null : () => setMethod(item.key)}
              >
                {item.text}
              </span>
            </div>
          </div>
        ))}

        {features.special_export.config?.turf?.help && root ? (
          <SidebarPopup
            id="export"
            root={root}
            help={features.special_export.config?.turf?.help.replace(
              '{{itemsPerSet}}',
              question.items_per_set || DEFAULT_ITEMS_PER_SET
            )}
            width="300px"
          />
        ) : null}

        {methodObj.exportOptions.threshold && null !== threshold ? (
          <div className={`${style.row} ${style['row-indent']} ${style['row-slider']}`}>
            <div className="p-fluid p-col-12">
              <Slider
                data-testid={`stat-exports-${exportType}-threshold`}
                min={0}
                max={THRESHOLD_OPTIONS.length - 1}
                value={THRESHOLD_OPTIONS.findIndex(value => value.key == threshold) || 0}
                labelTemplate={value => THRESHOLD_OPTIONS[value].text}
                disabled={disabled}
                onChange={disabled ? null : event => setThreshold(THRESHOLD_OPTIONS[event.value].key)}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid={`stat-exports-${exportType}-export`}
            className="p-button-rounded p-button-blue"
            label={EXPORT_TYPES.includes(processing) ? 'Downloading' : 'Download file'}
            loading={EXPORT_TYPES.includes(processing)}
            disabled={null !== processing || disabled}
            onClick={null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

SpecificMdOptions.propTypes = {
  root: PropTypes.any,
  report: PropTypes.object,
  features: PropTypes.object,
  question: PropTypes.object,
  options: PropTypes.array,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const SpecificTurfOptions = ({ report, options, exportType, processing, disabled, onExport }) => {
  const [method, setMethod] = useState(options[0].key)

  const methodObj = useMemo(() => options.find(option => option.key == method) || null, [method, options])

  const onExportClick = useCallback(() => {
    'function' == typeof onExport &&
      onExport({
        fileType: methodObj.fileType,
        exportType: methodObj.exportType,
        options: { ...methodObj.exportOptions, method }
      })
  }, [methodObj, method, onExport])

  return (
    <>
      <div className={style.section}>
        {options.map((item, index) => (
          <div key={`${item.key}-${index}`} className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid={`stat-exports-${exportType}-method-${item.key}`}
                className="p-radiobutton-sm"
                aria-labelledby={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                value={item.key}
                checked={method == item.key}
                disabled={disabled}
                onChange={disabled ? null : () => setMethod(item.key)}
              />
              <span
                data-testid={`stat-exports-${exportType}-method-${item.key}-label`}
                id={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': disabled
                })}
                onClick={disabled ? null : () => setMethod(item.key)}
              >
                {item.text}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid={`stat-exports-${exportType}-export`}
            className="p-button-rounded p-button-blue"
            label={EXPORT_TYPES.includes(processing) ? 'Downloading' : 'Download file'}
            loading={EXPORT_TYPES.includes(processing)}
            disabled={null !== processing || disabled}
            onClick={null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

SpecificTurfOptions.propTypes = {
  report: PropTypes.object,
  options: PropTypes.array,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const Specific = ({ root, model, data, report, features, exportType, processing, disabled, onExport }) => {
  const [exportQuestion, setExportQuestion] = useState(null)

  const exportQuestionRef = useRef(null)
  exportQuestionRef.current = exportQuestion

  // the dropdown shows one question per line and filters on `search`; authoring markup would both
  // restyle the line and break a match that spans the tag
  const exportQuestionsOptions = useMemo(
    () =>
      (data[exportType] || []).map(option => ({
        ...option,
        text: removeTagsFromText(option.text),
        search: removeTagsFromText(option.search)
      })),
    [data, exportType]
  )

  const exportQuestionObj = useMemo(() => {
    const option = exportQuestionsOptions.find(option => option.key == exportQuestion)
    if (option == undefined) return null

    return option
  }, [exportQuestion, exportQuestionsOptions])

  const exportQuestionOptions = useMemo(() => {
    if (null === exportQuestionObj) return null

    const exportOptionsDefaults = {
      key: exportQuestionObj.key,
      link: exportQuestionObj.link || null,
      position: exportQuestionObj.position
    }

    if ('cb' == exportQuestionObj.type) {
      return {
        key: exportType,
        fileType: 'binary',
        exportType: 'turf',
        exportOptions: {
          ...exportOptionsDefaults,
          turfType: 'answers'
        }
      }
    }

    if (['emd', 'cl', 'tl'].includes(exportQuestionObj.type)) {
      return {
        key: exportType,
        fileType: 'emd' == exportQuestionObj.type ? 'csv' : 'xlsx',
        exportType: LAB_EXPORT_TYPES[exportType] || exportType,
        exportOptions: {
          ...exportOptionsDefaults
        }
      }
    }

    if ('ec' == exportQuestionObj.type) {
      return [
        {
          key: 'question',
          text: 'Raw Events',
          fileType: 'csv',
          exportType: 'question',
          exportOptions: { ...exportOptionsDefaults }
        }
      ]
    }

    if ('cjc' == exportQuestionObj.type) {
      return [
        {
          key: 'conjointPkgs',
          text: 'Ranked List of Packages',
          fileType: 'csv',
          exportType: 'conjointPkgs',
          exportOptions: { ...exportOptionsDefaults }
        },
        {
          key: 'conjointDesign',
          text: 'Design Sheet by aytm',
          fileType: 'csv',
          exportType: 'conjointDesign',
          exportOptions: { ...exportOptionsDefaults }
        }
      ]
    }

    if ('cjd' == exportQuestionObj.type) {
      return [
        {
          key: 'conjoint',
          text: 'Raw Utility Scores',
          fileType: 'csv',
          exportType: 'conjoint',
          exportOptions: { ...exportOptionsDefaults }
        },
        {
          key: 'conjointPkgs',
          text: 'Ranked List of Packages',
          fileType: 'csv',
          exportType: 'conjointPkgs',
          exportOptions: { ...exportOptionsDefaults }
        },
        {
          key: 'conjointDesign',
          text: 'Design Sheet by aytm',
          fileType: 'csv',
          exportType: 'conjointDesign',
          exportOptions: { ...exportOptionsDefaults }
        }
      ]
    }

    if ('md' == exportQuestionObj.type) {
      return Object.entries(features.special_export.config?.turf?.available).map(([key, value]) => {
        return {
          key,
          text: value.text.replace('{{itemsPerSet}}', exportQuestionObj.items_per_set || DEFAULT_ITEMS_PER_SET),
          fileType: value.fileType,
          exportType: 'turf',
          exportOptions: {
            ...exportOptionsDefaults,
            threshold: value.threshold || false,
            methodText: value.xlsxText.replace(
              '{{itemsPerSet}}',
              exportQuestionObj.items_per_set || DEFAULT_ITEMS_PER_SET
            )
          }
        }
      })
    }

    const options = []
    const exportOptions = {
      ...exportOptionsDefaults,
      turfType: 'batches'
    }
    // A not-applicable answer (positive: false) is never a box, so positions count the substantive answers only.
    const answerVariants = exportQuestionObj.batches[0].answerVariants.filter(
      av => !Object.hasOwn(av, 'positive') || av.positive
    )

    options.push({
      key: 'bottom1',
      text: `TURF TOP 1 box (A${answerVariants.length})`,
      fileType: 'binary',
      exportType: 'turf',
      exportOptions: { method: 'top1', positive: [answerVariants.length], ...exportOptions }
    })
    if (3 < answerVariants.length) {
      options.push({
        key: 'bottom2',
        text: `TURF TOP 2 box (A${answerVariants.length - 1} & A${answerVariants.length})`,
        fileType: 'binary',
        exportType: 'turf',
        exportOptions: {
          method: 'top2',
          positive: [answerVariants.length - 1, answerVariants.length],
          ...exportOptions
        }
      })
      options.push({
        key: 'top2',
        text: `TURF TOP 2 box (A1 & A2)`,
        fileType: 'binary',
        exportType: 'turf',
        exportOptions: { method: 'top2', positive: [1, 2], ...exportOptions }
      })
    }
    options.push({
      key: 'top1',
      text: `TURF TOP 1 box (A1)`,
      fileType: 'binary',
      exportType: 'turf',
      exportOptions: { method: 'top1', positive: [1], ...exportOptions }
    })

    return options
  }, [exportType, exportQuestionObj, features.special_export])

  // A title longer than the row is cut short, so the whole of it is carried on the row itself for
  // the reader to hover -- as plain text, since the label it is cut from is authored markup.
  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span title={removeTagsFromText(option.label)} dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span
        title={removeTagsFromText(`${option.key.toUpperCase()}. ${option.text}`)}
        className={option.icon ? 'p-item-label' : ''}
      >
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: `${option.key.toUpperCase()}. ${option.text}` }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon && <i className={`icon-aytmfd-${option.icon}`} />}
        <span dangerouslySetInnerHTML={{ __html: `${option.key.toUpperCase()}. ${option.text}` }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  useEffect(() => {
    if (0 < exportQuestionsOptions.length) setExportQuestion(exportQuestionsOptions[0].key)
  }, [exportType, exportQuestionsOptions])

  // auto-force a fresh export for filtered conjoint — cached results would be stale across filter changes
  const onExportClick = useCallback(
    payload =>
      onExport({
        ...payload,
        props: {
          ...payload.props,
          noCache:
            payload.props?.noCache ||
            (0 < model.filtersPattern.length && ['maxdiff', 'conjoint'].includes(payload.exportType))
        }
      }),
    [model.filtersPattern, onExport]
  )

  return (
    <>
      {0 < exportQuestionsOptions.length ? (
        'turf' == exportType && features.turf.disabled ? (
          <div className="p-component p-button p-button-rounded p-button-light-blue p-disabled">
            <i className="icon-aytmfd-lock"></i>
            <span className="p-button-label p-c">Select a question</span>
          </div>
        ) : (
          <div className={style.row}>
            {1 < exportQuestionsOptions.length ? (
              <div className="p-fluid p-col-12">
                <SelectOverlay
                  data-testid={`stat-exports-${exportType}-question`}
                  className="p-dropdown-rounded p-dropdown-light-blue"
                  placeholder="Select a question"
                  value={exportQuestion}
                  options={exportQuestionsOptions}
                  itemTemplate={itemTemplate}
                  valueTemplate={valueTemplate}
                  filter
                  filterBy="search,text"
                  filterPlaceholder="Search items"
                  disabled={disabled}
                  onChange={setExportQuestion}
                />
              </div>
            ) : (
              <div className="p-fluid p-col-12">
                <div
                  data-testid={`stat-exports-${exportType}-question`}
                  className="p-component p-button p-button-rounded p-button-light-blue p-disabled"
                >
                  {exportQuestionsOptions[0].icon && <i className={`icon-aytmfd-${exportQuestionsOptions[0].icon}`} />}
                  <span
                    className="p-button-label p-c"
                    dangerouslySetInnerHTML={{
                      __html: `${exportQuestionsOptions[0].key.toUpperCase()}. ${exportQuestionsOptions[0].text}`
                    }}
                    style={{ display: 'inline-block', textAlign: 'left' }}
                  ></span>
                </div>
              </div>
            )}
          </div>
        )
      ) : null}

      {null !== exportQuestionObj ? (
        ['cb', 'emd', 'cl', 'tl'].includes(exportQuestionObj.type) ? (
          <SpecificOptions
            report={report}
            options={exportQuestionOptions}
            exportType={exportType}
            processing={processing}
            disabled={disabled}
            onExport={onExportClick}
          />
        ) : 'md' == exportQuestionObj.type ? (
          <SpecificMdOptions
            root={root}
            report={report}
            features={features}
            question={exportQuestionObj}
            options={exportQuestionOptions}
            exportType={exportType}
            processing={processing}
            disabled={disabled}
            onExport={onExportClick}
          />
        ) : (
          <SpecificTurfOptions
            report={report}
            options={exportQuestionOptions}
            exportType={exportType}
            processing={processing}
            disabled={disabled}
            onExport={onExportClick}
          />
        )
      ) : null}
    </>
  )
}

Specific.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  data: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

Specific.displayName = 'Specific'

export default Specific
